import React, { FC } from "react";
import Link from "next/link";
import Tealium from "@4tn/webx-analytics";
import { styled } from "@mui/material";
import SocialMedia from "@navigation/SocialMedia";
import Subscription from "@navigation/Subscription";
import { useQuery } from "@tanstack/react-query";
import { ButtonContainer } from "@common/Buttons";
import ErrorBoundary from "@common/ErrorBoundary/ErrorBoundary";
import { footerTalpaRightsText, maxPageContentWidth } from "@constants/consts";
import { colors, fonts } from "@constants/cssVariables";
import { TEALIUM_EVENT_CATEGORY, TEALIUM_EVENT_NAME } from "@constants/tealium.constants";
import { useHostConfig } from "@utils/common/getHostConfig";
import { useNoticeError } from "@utils/common/newRelic";
import { fetchByEndpoint } from "@utils/pageContent/fetchData";

const footerQueryKey = "footer";

const FooterWrapper = styled("div")(({ theme }) => ({
  backgroundColor: colors.neutral90,
  color: colors.neutral0,
  width: "100%",
  marginTop: theme.spacing(20),
  padding: theme.spacing(10, 0, 12),

  div: {
    paddingTop: 0,
  },

  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(12, 0),
  },
}));

const ItemsWrapper = styled("div")(({ theme }) => ({
  maxWidth: maxPageContentWidth,
  margin: "0 auto",
  padding: theme.spacing(0, 4),

  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(0, 20),
  },

  [theme.breakpoints.up("lg")]: {
    padding: theme.spacing(0, 24),
  },
}));

const SubmenuContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(12, 1fr)",
  gap: theme.spacing(4),
  rowGap: theme.spacing(8),
}));

const SubmenuItems = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gridColumn: "12 span",
  gap: theme.spacing(8),

  [theme.breakpoints.up("md")]: {
    gridColumn: "6 span",
  },

  [theme.breakpoints.up("lg")]: {
    gap: theme.spacing(4),
    gridColumn: "9 span",
    flexDirection: "row",
  },
}));

const MarketingItems = styled("div")(({ theme }) => ({
  gridColumn: "12 span",

  [`${Submenu}`]: {
    [`${SubmenuItemTitle}:hover`]: {
      color: colors.neutral30,
    },
  },

  [theme.breakpoints.up("md")]: {
    gridColumn: "6 span",
  },

  [theme.breakpoints.up("lg")]: {
    gridColumn: "3 span",
  },
}));

const Submenu = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),

  [`> ${ButtonContainer}`]: {
    width: 200,
    marginTop: theme.spacing(2),
    [theme.breakpoints.up("lg")]: {
      width: 177,
    },
  },

  [theme.breakpoints.up("md")]: {
    gap: theme.spacing(2),
    flex: 1,
  },

  [theme.breakpoints.up("lg")]: {
    flex: 1,
  },
}));

const Socials = styled(Submenu)(({ theme }) => ({
  gap: theme.spacing(5),
}));

const SubmenuTitle = styled("div")(({ theme }) => ({
  font: fonts.header5,
  color: colors.neutral0,
  marginBottom: theme.spacing(0.5),
}));

const SubmenuItemTitle = styled("div")(() => ({
  font: fonts.footer,
  color: colors.neutral30,
  ":hover": {
    color: colors.neutral0,
  },
}));

const RightsText = styled("div")(({ theme }) => ({
  font: fonts.bodySmall,
  marginTop: theme.spacing(10),
  [theme.breakpoints.up("md")]: {
    width: 732,
  },
  [theme.breakpoints.up("lg")]: {
    width: 1040,
  },
  [theme.breakpoints.up("xl")]: {
    width: 1248,
  },
}));

const trackClick = (eventLabel: string) => {
  Tealium.link({
    event_category: TEALIUM_EVENT_CATEGORY.FOOTER,
    event_name: TEALIUM_EVENT_NAME.FOOTER_CLICK,
    event_label: eventLabel,
  });
};

const Footer: FC<{ footer?: Navigation | null }> = ({ footer }) => {
  const { socialMedia } = useHostConfig();

  const currentYear = new Date().getFullYear();

  const { data: items, error } = useQuery({
    queryKey: [footerQueryKey],
    queryFn: () => fetchByEndpoint<Navigation>("/api/navigation/footer"),
    refetchOnWindowFocus: false,
    initialData: footer,
    select: (data) => data?.items,
    enabled: !footer,
  });

  useNoticeError(error, { queryKey: footerQueryKey });

  if (!items) return null;

  return (
    <FooterWrapper>
      <ItemsWrapper>
        <Subscription />
        <SubmenuContainer>
          <SubmenuItems>
            {items.map((submenu) => (
              <React.Fragment key={submenu.id}>
                {submenu.type === "submenu" && (
                  <Submenu>
                    <SubmenuTitle>{submenu.title}</SubmenuTitle>
                    {submenu.items.map((submenuItem) => (
                      <React.Fragment key={submenuItem.id}>
                        {submenuItem.type !== "submenu" && (
                          <Link
                            key={submenuItem.id}
                            href={submenuItem.url}
                            target={submenuItem.target}
                            onClick={() => trackClick(submenuItem.title)}
                            suppressHydrationWarning={submenuItem.url.startsWith("#")}
                          >
                            <SubmenuItemTitle>{submenuItem.title}</SubmenuItemTitle>
                          </Link>
                        )}
                      </React.Fragment>
                    ))}
                  </Submenu>
                )}
              </React.Fragment>
            ))}
          </SubmenuItems>
          <MarketingItems>
            <Socials>
              <SocialMedia {...socialMedia} />
            </Socials>
          </MarketingItems>
        </SubmenuContainer>
        <RightsText>&copy; {`${currentYear} ${footerTalpaRightsText}`}</RightsText>
      </ItemsWrapper>
    </FooterWrapper>
  );
};

const SafeFooter: FC<{ footer?: Navigation | null }> = (props) => (
  <ErrorBoundary onError={() => <></>}>
    <Footer {...props} />
  </ErrorBoundary>
);

export default SafeFooter;
